import { useHomepageHydration } from "../../context/HomepageRenderContext";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { fetchAccountSummary } from "../../api/accountApi";
import { getAccessToken } from "../../utils/authTokens";
import { AUTH_EVENT, getActiveSession } from "../../utils/nightStayAccount";

const formatCoins = (value) => Number(value || 0).toLocaleString("en-IN");

const formatMoney = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

const formatDate = (value) => {
  if (!value) return "No expiry set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No expiry set";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatEarnRule = (policy = null) => {
  const earn = policy?.earn || {};
  if (!earn.enabled) return "Coin earning is currently paused.";
  if (earn.rateType === "flat") return `${formatCoins(earn.value)} coins on eligible bookings`;
  return `${Number(earn.value || 0)}% coins on eligible bookings`;
};

const getValidityDays = (rewards = null) => {
  const days = Number(rewards?.activePolicy?.validity?.days || rewards?.validity?.days || 365);
  return Number.isFinite(days) && days > 0 ? Math.floor(days) : 365;
};

const getAvailableBalance = (summary = null) =>
  Number(summary?.rewards?.wallet?.availableBalance ?? summary?.counts?.rewardCoins ?? 0);

const RewardCoinIcon = ({ className = "" }) => (
  <span
    className={`relative inline-flex h-7 w-7 items-center justify-center rounded-full border border-amber-200 bg-amber-100 text-amber-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.75),0_4px_12px_rgba(217,119,6,0.18)] ${className}`}
    aria-hidden="true"
  >
    <span className="absolute inset-[4px] rounded-full border border-amber-300/80 bg-gradient-to-br from-yellow-200 via-amber-100 to-orange-200" />
    <i className="fa-solid fa-leaf relative text-[11px]" aria-hidden="true" />
  </span>
);

const EmptyExpiryState = () => (
  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-600">
    No active coin expiry lots are available yet. New earned coins will show their expiry date here.
  </div>
);

const RewardModal = ({ open, onClose, summary, status, error, signedIn, onSignIn }) => {
  const rewards = summary?.rewards || null;
  const wallet = rewards?.wallet || {};
  const activePolicy = rewards?.activePolicy || null;
  const expiringLots = Array.isArray(rewards?.expiringLots) ? rewards.expiringLots : [];
  const sameBooking = activePolicy?.sameBookingRedemption || null;
  const validityDays = getValidityDays(rewards);
  const availableBalance = getAvailableBalance(summary);

  useEffect(() => {
    if (!open || typeof window === "undefined") return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, open]);

  if (!open) return null;

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center overflow-y-auto bg-slate-950/45 p-3 backdrop-blur-sm sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="reward-coin-modal-title"
        className="my-auto max-h-[calc(100svh-2rem)] w-full max-w-2xl overflow-hidden rounded-3xl border border-white/70 bg-white shadow-[0_30px_80px_rgba(15,23,42,0.28)]"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 bg-gradient-to-br from-emerald-50 via-white to-amber-50 px-5 py-5">
          <div className="flex min-w-0 gap-3">
            <RewardCoinIcon className="mt-0.5 h-11 w-11" />
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">Eco coins</p>
              <h2 id="reward-coin-modal-title" className="mt-1 text-xl font-semibold text-slate-950">
                {signedIn ? `${formatCoins(availableBalance)} coins available` : "Earn coins on bookings"}
              </h2>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                {signedIn
                  ? `Coins are valid for ${validityDays} days from the date they become usable.`
                  : "Sign in to view your balance, expiry dates, and redemption history."}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50"
            aria-label="Close rewards information"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </div>

        <div className="max-h-[calc(92svh-112px)] overflow-y-auto px-5 py-5">
          {status === "loading" && signedIn ? (
            <div className="mb-4 flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-600">
              <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
              Loading your coin wallet...
            </div>
          ) : null}

          {error ? (
            <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
              {error}
            </div>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-700">Available</p>
              <p className="mt-2 text-2xl font-semibold text-emerald-950">{formatCoins(wallet.availableBalance)}</p>
              <p className="mt-1 text-xs text-emerald-800">Ready to use on eligible bookings</p>
            </div>
            <div className="rounded-2xl border border-sky-100 bg-sky-50 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-sky-700">Pending</p>
              <p className="mt-2 text-2xl font-semibold text-sky-950">{formatCoins(wallet.pendingBalance)}</p>
              <p className="mt-1 text-xs text-sky-800">Will unlock after the trip conditions are met</p>
            </div>
            <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-700">Validity</p>
              <p className="mt-2 text-2xl font-semibold text-amber-950">{validityDays} days</p>
              <p className="mt-1 text-xs text-amber-800">Shown before payment when coins are applied</p>
            </div>
          </div>

          <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(220px,0.75fr)]">
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-slate-950">How to use coins</h3>
              <div className="mt-3 space-y-3">
                {[
                  ["Earn", activePolicy ? formatEarnRule(activePolicy) : "Earn coins on confirmed eligible bookings."],
                  ["Unlock", "Some earned coins unlock after your trip or cancellation window."],
                  ["Redeem", "Apply usable coins at checkout before payment."],
                ].map(([label, copy]) => (
                  <div key={label} className="flex gap-3">
                    <span className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-semibold text-white">
                      {label.slice(0, 1)}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{label}</p>
                      <p className="text-xs leading-5 text-slate-600">{copy}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <h3 className="text-sm font-semibold text-slate-950">Same-booking use</h3>
              <div className="mt-3 rounded-xl bg-white px-3 py-2 shadow-sm">
                <p className="text-sm font-semibold text-slate-900">
                  {sameBooking?.enabled ? sameBooking.label || "Use newly earned EcoCoins immediately" : "Normal activation applies"}
                </p>
                <p className="mt-0.5 text-xs leading-5 text-slate-600">
                  {sameBooking?.enabled
                    ? `From ${formatMoney(sameBooking.minimumEligibleAmount || 0)} eligible value. Choose up to the maximum allowed coins at checkout.`
                    : "Newly earned coins cannot be used on the same booking."}
                </p>
                {sameBooking?.enabled ? <p className="mt-1 text-xs font-semibold text-emerald-700">Pre-GST EcoCoin redemption; not an automatic discount.</p> : null}
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-white">
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-950">Upcoming expiries</h3>
                <p className="text-xs text-slate-500">Nearest active coin lots first</p>
              </div>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                {expiringLots.length} lot{expiringLots.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="max-h-56 overflow-y-auto p-3">
              {expiringLots.length ? (
                <div className="space-y-2">
                  {expiringLots.map((lot) => (
                    <div
                      key={lot.id || `${lot.expiresAt}-${lot.source?.bookingReference}`}
                      className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">
                          {lot.source?.bookingReference || lot.source?.quoteCode || "Reward coins"}
                        </p>
                        <p className="text-xs text-slate-500">
                          {lot.status === "pending" ? "Pending" : "Available"} · Expires {formatDate(lot.expiresAt)}
                        </p>
                      </div>
                      <span className="flex-shrink-0 rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-800 shadow-sm">
                        {formatCoins(lot.remainingAmount ?? lot.amount)} coins
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyExpiryState />
              )}
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            {signedIn ? (
              <Link
                to="/account/rewards"
                onClick={onClose}
                className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                View coin balance
                <i className="fa-solid fa-arrow-right text-[11px]" aria-hidden="true" />
              </Link>
            ) : (
              <button
                type="button"
                onClick={onSignIn}
                className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                Sign in to view coins
                <i className="fa-solid fa-arrow-right text-[11px]" aria-hidden="true" />
              </button>
            )}
            <p className="text-xs leading-5 text-slate-500">
              Checkout shows the exact coins applied and rupee savings before payment.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return modal;
  return createPortal(modal, document.body);
};

const RewardCoinPill = ({ className = "" }) => {
  const hydrating = useHomepageHydration();
  const [session, setSession] = useState(() => getActiveSession());
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const signedIn = !hydrating && Boolean(session && getAccessToken());

  const loadSummary = useCallback(async () => {
    if (!signedIn) {
      setSummary(null);
      setStatus("idle");
      setError("");
      return;
    }
    setStatus("loading");
    setError("");
    try {
      const response = await fetchAccountSummary();
      setSummary(response || null);
      setStatus("ready");
    } catch (requestError) {
      setStatus("error");
      setError(requestError?.message || "Unable to load your coin wallet.");
    }
  }, [signedIn]);

  useEffect(() => {
    const handleAuthChange = () => setSession(getActiveSession());
    window.addEventListener(AUTH_EVENT, handleAuthChange);
    return () => window.removeEventListener(AUTH_EVENT, handleAuthChange);
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleRefresh = () => loadSummary();
    window.addEventListener("ecotour:rewards-refresh", handleRefresh);
    return () => window.removeEventListener("ecotour:rewards-refresh", handleRefresh);
  }, [loadSummary]);

  const availableBalance = useMemo(() => getAvailableBalance(summary), [summary]);

  const handleClick = () => {
    setIsOpen(true);
    if (signedIn && status !== "loading") loadSummary();
  };

  const handleSignIn = () => {
    setIsOpen(false);
    window.dispatchEvent(
      new CustomEvent("portal-auth:open", {
        detail: {
          reason: "rewards",
          mode: "login",
          message: "Login to view your Eco coins and expiry dates.",
        },
      }),
    );
  };

  return (
    <>
      <button
        type="button"
        className={`group inline-flex h-9 items-center gap-1.5 rounded-full border border-amber-200 bg-white px-1.5 pr-2 text-[12px] font-semibold text-slate-900 shadow-sm transition hover:-translate-y-[1px] hover:border-amber-300 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/70 ${className}`}
        onClick={handleClick}
        aria-label={signedIn ? `Open Eco coins wallet, ${formatCoins(availableBalance)} coins available` : "Open Eco coins information"}
      >
        <RewardCoinIcon />
        <span className="font-semibold">{signedIn ? formatCoins(availableBalance) : "Coins"}</span>
        <span className="hidden text-[11px] text-slate-500 md:inline">Eco</span>
      </button>
      <RewardModal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        summary={summary}
        status={status}
        error={error}
        signedIn={signedIn}
        onSignIn={handleSignIn}
      />
    </>
  );
};

export default RewardCoinPill;
